import { EMOTES } from '@sw/protocol';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleAck } from '../lib/errors';
import { emit } from '../net/socket';
import { useChat } from '../stores/chat';
import { useSession } from '../stores/session';
import { Button } from '../ui/Button';
import ui from '../ui/ui.module.css';
import s from './ChatPanel.module.css';

export function ChatPanel() {
  const { t } = useTranslation();
  const messages = useChat((c) => c.messages);
  const me = useSession((x) => x.user?.id);
  const [text, setText] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  async function send() {
    const v = text.trim();
    if (!v) return;
    setText('');
    handleAck(await emit('chat:send', { text: v }));
  }

  return (
    <div className={s.chat}>
      <div className={s.list} ref={listRef} data-testid="chat-list">
        {messages.length === 0 ? <div className={s.empty}>{t('chat.empty')}</div> : null}
        {messages.map((m) =>
          m.kind === 'system' ? (
            <div key={m.id} className={s.system}>
              {t(`system.${m.code}`, { defaultValue: m.code, ...(m.params ?? {}) })}
            </div>
          ) : (
            <div key={m.id} className={[s.msg, m.userId === me ? s.me : ''].join(' ')}>
              <span className={s.author}>{m.username}</span>
              {m.kind === 'text' ? m.text : <span className={s.emote}>{t(`emotes.${m.emote}`)}</span>}
            </div>
          ),
        )}
      </div>
      <div className={s.emotes}>
        {EMOTES.map((e) => (
          <button key={e} type="button" className={s.emoteBtn} onClick={() => void emit('chat:emote', { emote: e }).then(handleAck)}>
            {t(`emotes.${e}`)}
          </button>
        ))}
      </div>
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input className={ui.input} value={text} maxLength={300} placeholder={t('chat.placeholder')} onChange={(e) => setText(e.target.value)} />
        <Button type="submit" size="small" disabled={!text.trim()}>
          {t('chat.send')}
        </Button>
      </form>
    </div>
  );
}
